import type {
  ImageBuffer,
  LeafPrimitive,
  LocalizedRichTextComponent,
  LocalizedTextComponent,
  LocalizedVariant,
  PrinterProfile,
  ResolvedImage,
  SymbolComponent,
  TextComponent,
  VariableMeta,
  Warning,
} from '@tsagko/label-contracts'
import { SYMBOLOGY_METRICS } from '@tsagko/label-contracts'
import type { FlatZone } from './flatten-zones'
import { derivePayloadForCode128, validateCode128Payload } from './barcodes/code-128'
import { derivePayloadForEan13 } from './barcodes/ean-13'
import { derivePayloadForGs1128 } from './barcodes/gs1-128'
import { derivePayloadForItf14 } from './barcodes/itf-14'
import { derivePayloadForQr } from './barcodes/qr'
import { escapeZplText } from './escape-zpl-text'
import {
  A0_VMETRICS,
  barcodeIntrinsicSize,
  capBandAnchorY,
  DEFAULT_PLACEMENT,
  hAlignToPlan,
  HRI_GAP_MM,
  type PlanRotation,
  resolveAnchor,
  textIntrinsicSize,
  rotationToPlan,
  textPlanGeometry,
} from './internal/intrinsic-size'
import { mmToDots, resolveValueSource } from './internal/units'

/**
 * Layout-resolved, unit-agnostic description of what to render at a given
 * anchor on the label. Consumed by:
 *   - `commandsFromPlans` → ZPL emitter (mm → dots via printer dpi)
 *   - `<RenderPlanItem />` in the designer canvas (mm → px via pxPerMm)
 *
 * Anchors are always TOP-LEFT in mm (matching ZPL `^FO` semantics, NOT
 * CSS flex-centering). Sizes are mm where both renderers can convert.
 *
 * IDs are stable within a single `buildRenderPlan` invocation. Single-
 * primitive plans use the source `id`; `localized-rich-text` expansions
 * use `<primitive.id>:variant:<index>` (0-indexed, matching
 * `component.variants` order, including positions of empty variants that
 * got skipped); the pipeline-emitted barcode human-readable line uses
 * `<primitive.id>:hri`.
 */
interface PlanBase {
  id: string
}

export type RenderPlan =
  | (PlanBase & {
      kind: 'text'
      /**
       * `^FO` anchor = the TOP-LEFT corner of the (rotated) text block's
       * page bounding box. For rotated text this is paired with a mandatory
       * `^FB` whose width acts along the text's local flow axis — see
       * `textPlanGeometry` for the probe-verified origin convention.
       */
      anchorXMm: number
      anchorYMm: number
      text: string
      fontHeightMm: number
      fontWidthMm: number
      fontId: string
      /**
       * `^FB` letter in the text's LOCAL frame. For rotation I/B the
       * planner already mirrored the operator's page-space placement
       * (I reads right-to-left, B bottom-up), so emit/render it verbatim.
       */
      alignment: 'L' | 'C' | 'R' | 'J'
      /** `^FB` block width along the local flow axis: the region's width
       *  for N/I, the region's HEIGHT for R/B. */
      wrapBoxWidthMm: number
      maxLines: number
      lineSpacingMm: number
      /** `^A0` orientation char: N=0°, R=90°cw, I=180°, B=270°. */
      rotation: PlanRotation
      /** Cap-band pin consumed by the canvas to position its own font's cap
       *  band along the text's LOCAL vertical axis: which `^A0` reference
       *  line ('top'=cap-top, 'middle'=cap-center, 'bottom'=baseline) the
       *  pipeline pinned when computing the cross-axis anchor. Equals the
       *  placement's vAlign for unrotated text; for rotated text it is the
       *  rotation-mapped cross-axis pin (see `textPlanGeometry`). Optional
       *  for backward-compatible plan literals (consumers default 'top'). */
      vAlign?: 'top' | 'middle' | 'bottom'
      /**
       * Bounding box of the enclosing region. When `regionBackgroundColor`
       * is `'black'`, the ZPL emitter prepends a filled `^GB` of this box
       * (drawing a black rectangle) and toggles `^FR` on the text so the
       * glyphs render in white-on-black. Carrier of the leaf zone's
       * computed mm box (xMm/yMm/widthMm/heightMm), threaded so the emitter
       * doesn't have to walk the template again.
       */
      regionXMm: number
      regionYMm: number
      regionWidthMm: number
      regionHeightMm: number
      regionBackgroundColor?: string
    })
  | (PlanBase & {
      kind: 'separator'
      anchorXMm: number
      anchorYMm: number
      widthMm: number
      heightMm: number
      thicknessMm: number
    })
  | (PlanBase & {
      kind: 'image'
      anchorXMm: number
      anchorYMm: number
      widthMm: number
      heightMm: number
      buffer: ImageBuffer | null
      widthDots?: number
      heightDots?: number
      placeholderLabel?: string
    })
  | (PlanBase & {
      kind: 'barcode'
      /**
       * Anchor + size describe the SYMBOL BOX: quiet zones + bars (per
       * GS1/ISO the quiet zone is part of the symbol). The first bar sits at
       * `anchorXMm + quietLeftMm` / `anchorYMm + quietTopMm` — the ZPL
       * emitter offsets `^FO` by exactly that, and the canvas insets its bar
       * rendering the same way. The pipeline-emitted human-readable line
       * (code-128 / gs1-128 with humanReadable on) is NOT part of this box —
       * it travels as its own `text` plan with id `<id>:hri`.
       */
      anchorXMm: number
      anchorYMm: number
      widthMm: number
      heightMm: number
      /** Quiet-zone insets in mm (0 when omitted — legacy plan literals). */
      quietLeftMm?: number
      quietRightMm?: number
      quietTopMm?: number
      quietBottomMm?: number
      /**
       * HRI band height (gap + HRI font cell) in mm — 0 when no pipeline HRI.
       * Part of the upright box ALONG the bars' length axis; the rotated `^FO`
       * inset needs it because the bars sit on the far side of this band.
       */
      hriBandMm?: number
      symbology: 'code-128' | 'ean-13' | 'qr' | 'gs1-128' | 'itf'
      data: string
      /**
       * Drives the NATIVE interpretation-line param (^BC / ^BE). Always
       * false for code-128 / gs1-128 — those render the pipeline's own
       * sized ^A0 HRI line instead. EAN-13 keeps the native ^BE line.
       */
      humanReadable: boolean
      moduleWidthDots: number
      heightDots: number
      /**
       * `^BC`/`^BE`/`^BQ` orientation char. 'N' for an unrotated label
       * (byte-identical legacy); 'R'/'I'/'B' carry whole-label rotation onto
       * the symbol. The on-label footprint swaps w/h for 'R'/'B'.
       */
      rotation: PlanRotation
      placeholderLabel?: string
      /**
       * Mirrors the text-plan fields above. See the text arm's docstring
       * for the reverse-field rendering contract.
       */
      regionXMm: number
      regionYMm: number
      regionWidthMm: number
      regionHeightMm: number
      regionBackgroundColor?: string
    })
  | (PlanBase & {
      kind: 'border-line'
      /**
       * Anchor (top-left of the border line's bounding rectangle) and length
       * along the boundary axis. `axis: 'vertical'` border draws DOWN
       * from anchor (lengthMm = height); `axis: 'horizontal'` draws RIGHT
       * (lengthMm = width). The cross-axis size equals `thicknessMm`.
       */
      anchorXMm: number
      anchorYMm: number
      lengthMm: number
      thicknessMm: number
      axis: 'horizontal' | 'vertical'
      style: 'solid' | 'dashed' | 'dotted'
    })

const PT_TO_MM = 25.4 / 72

/**
 * Plan one text primitive. Returns either one text plan or an empty array
 * for empty resolved values (silent skip — lint owns the warning).
 */
function planText(
  zone: FlatZone,
  component: TextComponent,
  data: Record<string, unknown>,
  profile: PrinterProfile,
  variables: Record<string, VariableMeta>
): { plan: RenderPlan | null; warnings: Warning[] } {
  const resolved = resolveValueSource(component.value, data, variables)
  if (resolved.length === 0) return { plan: null, warnings: [] }
  const escaped = escapeZplText(resolved, profile.encoding)
  const sizePt = component.sizePt ?? 10
  const fontHeightMm = sizePt * PT_TO_MM
  const maxLines = component.maxLines ?? 1
  const placement = component.placement ?? DEFAULT_PLACEMENT
  const rotation = rotationToPlan(component.rotation)
  // Anchor + ^FB letter/width + canvas pin, rotation-aware (issue #1).
  const geo = textPlanGeometry({
    region: { xMm: zone.xMm, yMm: zone.yMm, wMm: zone.widthMm, hMm: zone.heightMm },
    fontHeightMm,
    placement,
    rotation,
    lines: maxLines,
    lineSpacingMm: 0,
  })
  return {
    plan: {
      kind: 'text',
      id: component.id,
      anchorXMm: geo.anchorXMm,
      anchorYMm: geo.anchorYMm,
      text: escaped.value,
      fontHeightMm,
      fontWidthMm: fontHeightMm / 2,
      fontId: component.fontId ?? '0',
      alignment: geo.alignment,
      wrapBoxWidthMm: geo.wrapBoxWidthMm,
      maxLines,
      lineSpacingMm: 0,
      rotation,
      vAlign: geo.vAlign,
      regionXMm: zone.xMm,
      regionYMm: zone.yMm,
      regionWidthMm: zone.widthMm,
      regionHeightMm: zone.heightMm,
      regionBackgroundColor: zone.backgroundColor,
    },
    warnings: escaped.warnings,
  }
}

/**
 * Pick the localized variant whose `locale` matches the runtime locale.
 * Mirrors `build-commands.ts#pickLocalizedVariant`: `data._locale` wins,
 * `data.locale` is the fallback, and `variants[0]` is the final fallback.
 * Used by `planLocalizedText`. `planLocalizedRichText` emits ALL variants
 * (rich-text is a layout primitive, not a locale selector).
 */
function pickLocalizedVariant(
  variants: readonly LocalizedVariant[],
  data: Record<string, unknown>
): LocalizedVariant {
  const candidate =
    typeof data._locale === 'string'
      ? data._locale
      : typeof data.locale === 'string'
        ? data.locale
        : undefined
  if (candidate !== undefined) {
    const match = variants.find(v => v.locale === candidate)
    if (match) return match
  }
  return variants[0]!
}

function planLocalizedText(
  zone: FlatZone,
  component: LocalizedTextComponent,
  data: Record<string, unknown>,
  profile: PrinterProfile,
  variables: Record<string, VariableMeta>
): { plan: RenderPlan | null; warnings: Warning[] } {
  const variant = pickLocalizedVariant(component.variants, data)
  const resolved = resolveValueSource(variant.value, data, variables)
  if (resolved.length === 0) return { plan: null, warnings: [] }
  const escaped = escapeZplText(resolved, profile.encoding)
  const sizePt = component.sizePt ?? 10
  const fontHeightMm = sizePt * PT_TO_MM
  const placement = component.placement ?? DEFAULT_PLACEMENT
  const rotation = rotationToPlan(component.rotation)
  // Anchor + ^FB letter/width + canvas pin, rotation-aware (issue #1).
  const geo = textPlanGeometry({
    region: { xMm: zone.xMm, yMm: zone.yMm, wMm: zone.widthMm, hMm: zone.heightMm },
    fontHeightMm,
    placement,
    rotation,
  })
  return {
    plan: {
      kind: 'text',
      id: component.id,
      anchorXMm: geo.anchorXMm,
      anchorYMm: geo.anchorYMm,
      text: escaped.value,
      fontHeightMm,
      fontWidthMm: fontHeightMm / 2,
      fontId: component.fontId ?? '0',
      alignment: geo.alignment,
      wrapBoxWidthMm: geo.wrapBoxWidthMm,
      maxLines: 1,
      lineSpacingMm: 0,
      rotation,
      vAlign: geo.vAlign,
      regionXMm: zone.xMm,
      regionYMm: zone.yMm,
      regionWidthMm: zone.widthMm,
      regionHeightMm: zone.heightMm,
      regionBackgroundColor: zone.backgroundColor,
    },
    warnings: escaped.warnings,
  }
}

function planLocalizedRichText(
  zone: FlatZone,
  component: LocalizedRichTextComponent,
  data: Record<string, unknown>,
  profile: PrinterProfile,
  variables: Record<string, VariableMeta>
): { plans: RenderPlan[]; warnings: Warning[] } {
  const sizePt = component.sizePt ?? 10
  const fontHeightMm = sizePt * PT_TO_MM
  const layout = component.layout ?? 'stacked'
  const lineHeightMm = sizePt * 0.4
  const placement = component.placement ?? DEFAULT_PLACEMENT
  const columnWidthMm =
    layout === 'columns' && component.variants.length > 0
      ? zone.widthMm / component.variants.length
      : 0
  const plans: RenderPlan[] = []
  const warnings: Warning[] = []
  component.variants.forEach((variant, index) => {
    const resolved = resolveValueSource(variant.value, data, variables)
    if (resolved.length === 0) return
    const escaped = escapeZplText(resolved, profile.encoding)
    warnings.push(...escaped.warnings)
    // stacked OR grid (grid falls back to stacked in Phase 1 — mirrors
    // `build-commands.ts` behaviour).
    const variantRegion =
      layout === 'columns'
        ? {
            xMm: zone.xMm + index * columnWidthMm,
            yMm: zone.yMm,
            wMm: columnWidthMm,
            hMm: zone.heightMm,
          }
        : {
            xMm: zone.xMm,
            yMm: zone.yMm + index * lineHeightMm,
            wMm: zone.widthMm,
            hMm: lineHeightMm,
          }
    const intrinsic = textIntrinsicSize({
      fontHeightMm,
      maxLines: 1,
      lineSpacingMm: 0,
      wrapBoxWidthMm: variantRegion.wMm,
    })
    const { anchorXMm } = resolveAnchor(variantRegion, intrinsic, placement)
    const anchorYMm = capBandAnchorY(
      variantRegion.yMm,
      variantRegion.hMm,
      fontHeightMm,
      placement.vAlign,
      A0_VMETRICS
    )
    plans.push({
      kind: 'text',
      id: `${component.id}:variant:${index}`,
      anchorXMm,
      anchorYMm,
      text: escaped.value,
      fontHeightMm,
      fontWidthMm: fontHeightMm / 2,
      fontId: component.fontId ?? '0',
      alignment: hAlignToPlan(placement.hAlign),
      wrapBoxWidthMm: variantRegion.wMm,
      maxLines: 1,
      lineSpacingMm: 0,
      // `LocalizedRichTextComponent` has no `rotation` field in the
      // contract — rich-text is a multi-variant layout primitive, not a
      // single rotated field. Hardcode `'N'` to match the existing
      // build-commands.ts behaviour (which never emitted ^FW for rich-text).
      rotation: 'N',
      vAlign: placement.vAlign,
      regionXMm: zone.xMm,
      regionYMm: zone.yMm,
      regionWidthMm: zone.widthMm,
      regionHeightMm: zone.heightMm,
      regionBackgroundColor: zone.backgroundColor,
    })
  })
  return { plans, warnings }
}

/**
 * Warn when `requestedDots` exceeds the `^BY` maximum of 10. Clamping is
 * handled in `serialize.ts`; the warning surfaces to the operator so they
 * know the printed width will differ from what they configured.
 * `effectiveModuleWidthMm` is the resolved width (the component's own
 * `moduleWidthMm` or the registry default) so the message reports the value
 * that actually drove `requestedDots`, not a misleading `0.000mm`.
 */
function moduleWidthClampWarning(
  c: SymbolComponent,
  requestedDots: number,
  effectiveModuleWidthMm: number
): Warning[] {
  if (requestedDots <= 10) return []
  return [
    {
      code: 'PRINT_WIDTH_CLAMPED',
      severity: 'warn',
      message: `Bar width ${effectiveModuleWidthMm.toFixed(3)}mm exceeds the ^BY max (10 dots); clamped.`,
      path: [c.id],
      suggestion: 'Reduce the bar width.',
    },
  ]
}

/**
 * Warn when a barcode's intrinsic footprint exceeds its zone. The canvas
 * already clips the render to the top-left anchor (via `resolveAnchor`), so
 * no ZPL output changes — this is a pure advisory to the operator.
 */
function symbolOverflowWarnings(
  c: SymbolComponent,
  intrinsic: { wMm: number; hMm: number; hriBandMm?: number },
  useIntrinsic: boolean,
  region: { wMm: number; hMm: number }
): Warning[] {
  if (!useIntrinsic) return []
  const over = intrinsic.wMm > region.wMm + 0.01 || intrinsic.hMm > region.hMm + 0.01
  if (!over) return []
  // Whether the box actually includes an HRI band (effective default applied).
  const hri = (intrinsic.hriBandMm ?? 0) > 0
  return [
    {
      code: 'PLACEMENT_OVERFLOW',
      severity: 'warn',
      message:
        `Barcode (${c.symbology}) needs ${intrinsic.wMm.toFixed(1)}×${intrinsic.hMm.toFixed(1)}mm ` +
        `(bars plus the mandatory white quiet zone${hri ? ' and the human-readable line' : ''}) ` +
        `but its region is ${region.wMm.toFixed(1)}×${region.hMm.toFixed(1)}mm.`,
      path: [c.id],
      suggestion: hri
        ? 'Reduce the bar width, turn off the human-readable line, or enlarge the region. The quiet zone is required for scanning.'
        : 'Reduce the bar width or enlarge the region. The quiet zone is required for scanning.',
    },
  ]
}

/**
 * Shared tail of every renderable-symbology arm: compute the symbol box
 * (quiet zones + bars, plus the HRI band for placement/overflow), resolve
 * the anchor, accumulate warnings, and build the barcode plan — plus, for
 * code-128 / gs1-128 with humanReadable on, the pipeline's own sized ^A0
 * human-readable text plan (id `<componentId>:hri`) centered under the bars.
 *
 * `hriText` is the DISPLAY form (gs1-128: parenthesized `(AI)data…`;
 * code-128: the raw data — both matching the native interpretation line's
 * convention). Pass `undefined` for symbologies that keep their native line
 * (ean-13) or have none (qr).
 */
function buildSymbolPlans(
  zone: FlatZone,
  c: SymbolComponent,
  symbology: 'code-128' | 'ean-13' | 'qr' | 'gs1-128' | 'itf',
  payload: string,
  hriText: string | undefined,
  profile: PrinterProfile,
  warnings: Warning[]
): RenderPlan[] {
  const placement = c.placement ?? DEFAULT_PLACEMENT
  const box = barcodeIntrinsicSize(c, payload, profile.dpi)
  const useIntrinsic = box.wMm > 0 && box.hMm > 0
  const region = { xMm: zone.xMm, yMm: zone.yMm, wMm: zone.widthMm, hMm: zone.heightMm }
  // Whole-label rotation (resolvePrintRotation) composes onto the symbol
  // component, so a barcode on a rotated label arrives with c.rotation set.
  // 'R'/'B' (90/270) turn the symbol a quarter-turn, swapping its on-label
  // footprint (the box w/h trade places); 'N'/'I' keep the upright footprint.
  const rot = rotationToPlan(c.rotation)
  const footprint =
    rot === 'R' || rot === 'B' ? { wMm: box.hMm, hMm: box.wMm } : { wMm: box.wMm, hMm: box.hMm }
  // Anchor the FULL symbol box (quiet + bars + HRI band) so placement and
  // PLACEMENT_OVERFLOW stay honest about everything the symbol needs.
  const { anchorXMm, anchorYMm } = useIntrinsic
    ? resolveAnchor(region, footprint, placement)
    : { anchorXMm: zone.xMm, anchorYMm: zone.yMm }
  const effMm = c.moduleWidthMm ?? SYMBOLOGY_METRICS[symbology].defaultModuleWidthMm
  const requestedDots = mmToDots(effMm, profile.dpi)
  if (symbology !== 'qr') {
    // QR uses ^BQ magnification, not ^BY — no clamp warning (pre-existing).
    warnings.push(...moduleWidthClampWarning(c, requestedDots, effMm))
  }
  // Overflow compares the on-label footprint (rotated for R/B) to the region.
  warnings.push(
    ...symbolOverflowWarnings(
      c,
      { wMm: footprint.wMm, hMm: footprint.hMm, hriBandMm: box.hriBandMm },
      useIntrinsic,
      { wMm: zone.widthMm, hMm: zone.heightMm }
    )
  )
  // The native interpretation line stays only where we don't emit our own:
  // ean-13 (^BE's line is part of the EAN layout and scales with the symbol).
  // code-128 / gs1-128 always send N — their HRI is the text plan below.
  const nativeHri =
    symbology === 'code-128' || symbology === 'gs1-128'
      ? false
      : (c.humanReadable ?? SYMBOLOGY_METRICS[symbology].defaultHumanReadable)
  const plans: RenderPlan[] = [
    {
      kind: 'barcode',
      id: c.id,
      anchorXMm,
      anchorYMm,
      widthMm: useIntrinsic ? box.wMm : zone.widthMm,
      heightMm: useIntrinsic ? box.hMm - box.hriBandMm : zone.heightMm,
      quietLeftMm: useIntrinsic ? box.quietLeftMm : 0,
      quietRightMm: useIntrinsic ? box.quietRightMm : 0,
      quietTopMm: useIntrinsic ? box.quietTopMm : 0,
      quietBottomMm: useIntrinsic ? box.quietBottomMm : 0,
      hriBandMm: useIntrinsic ? box.hriBandMm : 0,
      symbology,
      data: payload,
      humanReadable: nativeHri,
      moduleWidthDots: Math.max(1, requestedDots),
      heightDots: mmToDots(c.heightMm ?? SYMBOLOGY_METRICS[symbology].defaultHeightMm, profile.dpi),
      rotation: rot,
      regionXMm: zone.xMm,
      regionYMm: zone.yMm,
      regionWidthMm: zone.widthMm,
      regionHeightMm: zone.heightMm,
      regionBackgroundColor: zone.backgroundColor,
    },
  ]
  if (useIntrinsic && hriText !== undefined && hriText.length > 0 && box.hriBandMm > 0) {
    const hriFontMm = box.hriBandMm - HRI_GAP_MM
    // The HRI line travels with the symbol under rotation. In the upright
    // frame it is the box's bottom strip (one HRI_GAP below the bars), centered
    // across the bar width. Rotating the symbol carries that strip around the
    // box. The text block's `^FO` is the rotated text block's bounding-box
    // top-left (like all `^FB`-paired text); its wrap-box width runs along the
    // text's flow axis (page-horizontal for N/I, page-vertical for R/B), so it
    // centers across the bars' length. Offsets derive from rotating the
    // upright HRI-cell rect (bottom hriFontMm strip, full box width) about the
    // box — see commands-from-plans for the matching bar-^FO derivation.
    //   N: HRI is the box BOTTOM strip   → top-left at +y(quietTop+bars+gap)
    //   R (90° CW):  HRI maps to box LEFT  strip → top-left at the box origin
    //   B (270° CCW):HRI maps to box RIGHT strip → top-left at +x(boxH−font)
    //   I (180°):    HRI maps to box TOP   strip → top-left at the box origin
    const hri = ((): {
      x: number
      y: number
      wrap: number
      align: 'L' | 'C' | 'R' | 'J'
    } => {
      switch (rot) {
        case 'R':
          return { x: anchorXMm, y: anchorYMm, wrap: box.wMm, align: 'C' }
        case 'B':
          return {
            x: anchorXMm + (footprint.wMm - hriFontMm),
            y: anchorYMm,
            wrap: box.wMm,
            align: 'C',
          }
        case 'I':
          return { x: anchorXMm, y: anchorYMm, wrap: box.wMm, align: 'C' }
        default: // 'N'
          return {
            x: anchorXMm,
            y: anchorYMm + box.quietTopMm + box.barsHeightMm + HRI_GAP_MM,
            wrap: box.wMm,
            align: 'C',
          }
      }
    })()
    plans.push({
      kind: 'text',
      id: `${c.id}:hri`,
      anchorXMm: hri.x,
      anchorYMm: hri.y,
      text: hriText,
      fontHeightMm: hriFontMm,
      // Natural ^A0 width (w = h) — matches the native line's look; the
      // canvas text pathway renders this as scaleX(1).
      fontWidthMm: hriFontMm,
      fontId: '0',
      alignment: hri.align,
      wrapBoxWidthMm: hri.wrap,
      maxLines: 1,
      lineSpacingMm: 0,
      rotation: rot,
      vAlign: 'top',
      regionXMm: zone.xMm,
      regionYMm: zone.yMm,
      regionWidthMm: zone.widthMm,
      regionHeightMm: zone.heightMm,
      regionBackgroundColor: zone.backgroundColor,
    })
  }
  return plans
}

/**
 * Plan one symbol primitive. Mirrors the per-symbology defaults that
 * `internal/compile-symbol.ts` applied (heightMm, moduleWidthMm,
 * humanReadable) and uses the same `derivePayloadFor*` helpers so the
 * resolved payload is byte-equivalent to what the old emitter produced.
 * Symbologies outside the Phase 1 set (`code-128 | ean-13 | qr | gs1-128 | itf`)
 * silently skip (return no plans); lint surface warns the operator.
 */
function planSymbol(
  zone: FlatZone,
  c: SymbolComponent,
  data: Record<string, unknown>,
  profile: PrinterProfile
): { plans: RenderPlan[]; warnings: Warning[] } {
  const warnings: Warning[] = []
  switch (c.symbology) {
    case 'code-128': {
      const rawPayload = derivePayloadForCode128(c.dataStructure, data)
      const escaped = escapeZplText(rawPayload, profile.encoding)
      warnings.push(...escaped.warnings)
      const validation = validateCode128Payload(escaped.value)
      if (!validation.ok) return { plans: [], warnings }
      // HRI shows the raw data verbatim — the native line's convention.
      return {
        plans: buildSymbolPlans(
          zone,
          c,
          'code-128',
          validation.payload,
          validation.payload,
          profile,
          warnings
        ),
        warnings,
      }
    }
    case 'ean-13': {
      const rawPayload = derivePayloadForEan13(c, data)
      if (rawPayload === undefined) return { plans: [], warnings }
      const escaped = escapeZplText(rawPayload, profile.encoding)
      warnings.push(...escaped.warnings)
      if (escaped.value.length === 0) return { plans: [], warnings }
      // EAN-13 keeps the NATIVE ^BE interpretation line → no hriText.
      return {
        plans: buildSymbolPlans(zone, c, 'ean-13', escaped.value, undefined, profile, warnings),
        warnings,
      }
    }
    case 'qr': {
      const rawPayload = derivePayloadForQr(c.dataStructure, data)
      if (rawPayload === undefined) return { plans: [], warnings }
      const escaped = escapeZplText(rawPayload, profile.encoding)
      warnings.push(...escaped.warnings)
      if (escaped.value.length === 0) return { plans: [], warnings }
      return {
        plans: buildSymbolPlans(zone, c, 'qr', escaped.value, undefined, profile, warnings),
        warnings,
      }
    }
    case 'gs1-128': {
      const derived = derivePayloadForGs1128(c.dataStructure, data, profile.encoding)
      warnings.push(...derived.warnings)
      if (derived.payload === undefined) return { plans: [], warnings }
      let finalPayload: string
      let hriText: string
      if (c.dataStructure.kind === 'gs1-ai') {
        finalPayload = derived.payload
        // Parenthesized `(AI)data…` form built alongside the payload.
        hriText = derived.hri ?? ''
      } else {
        const escaped = escapeZplText(derived.payload, profile.encoding)
        warnings.push(...escaped.warnings)
        finalPayload = escaped.value
        // Raw-ish payloads: strip hand-typed `>8` FNC1 markers for display.
        hriText = finalPayload.split('>8').join('')
      }
      if (finalPayload.length === 0) return { plans: [], warnings }
      return {
        plans: buildSymbolPlans(zone, c, 'gs1-128', finalPayload, hriText, profile, warnings),
        warnings,
      }
    }
    case 'itf': {
      const rawPayload = derivePayloadForItf14(c, data)
      if (rawPayload === undefined) return { plans: [], warnings }
      const escaped = escapeZplText(rawPayload, profile.encoding)
      warnings.push(...escaped.warnings)
      if (escaped.value.length === 0) return { plans: [], warnings }
      // ITF-14 keeps the NATIVE ^B2 interpretation line (like ean-13) → no hriText.
      return {
        plans: buildSymbolPlans(zone, c, 'itf', escaped.value, undefined, profile, warnings),
        warnings,
      }
    }
    case 'code-39':
    case 'ean-8':
    case 'upc-a':
    case 'datamatrix':
    case 'pdf417':
    case 'aztec-cc':
      // Phase 1: silent skip for unsupported symbologies; lint surface
      // emits the warning. This is a behavioral change from the prior
      // build-commands.ts (which threw); the change is intentional because
      // tier-1 designer cannot render a thrown exception gracefully.
      return { plans: [], warnings }
    default: {
      const _ex: never = c.symbology
      throw new Error(`planSymbol: unknown symbology ${JSON.stringify(_ex)}`)
    }
  }
}

/**
 * Build a `RenderPlan[]` from a flattened-zone list. Pure: no I/O, no
 * mutation. Deterministic for a given (flatZones, data, profile, images)
 * tuple. Empty resolved values produce zero plans (silent skip — lint
 * surface owns the warning).
 */
export function buildRenderPlan(
  flatZones: FlatZone[],
  data: Record<string, unknown>,
  profile: PrinterProfile,
  resolvedImages: Map<string, ResolvedImage>,
  variables: Record<string, VariableMeta> = {}
): { plans: RenderPlan[]; warnings: Warning[] } {
  const plans: RenderPlan[] = []
  const warnings: Warning[] = []
  for (const zone of flatZones) {
    const c: LeafPrimitive = zone.component
    switch (c.type) {
      case 'text': {
        const r = planText(zone, c, data, profile, variables)
        if (r.plan) plans.push(r.plan)
        warnings.push(...r.warnings)
        break
      }
      case 'localized-text': {
        const r = planLocalizedText(zone, c, data, profile, variables)
        if (r.plan) plans.push(r.plan)
        warnings.push(...r.warnings)
        break
      }
      case 'localized-rich-text': {
        const r = planLocalizedRichText(zone, c, data, profile, variables)
        plans.push(...r.plans)
        warnings.push(...r.warnings)
        break
      }
      case 'separator': {
        plans.push({
          kind: 'separator',
          id: c.id,
          anchorXMm: zone.xMm,
          anchorYMm: zone.yMm,
          widthMm: zone.widthMm,
          heightMm: zone.heightMm,
          thicknessMm: c.thicknessMm ?? 0.5,
        })
        break
      }
      case 'image': {
        // Keyed by component id (one resolved bitmap per placement). The
        // resolved entry carries its own dot dims (already fit/aspect-resolved
        // by the demo adapter), so render-plan does NO fit math: it converts
        // the dots to mm and places via the existing resolveAnchor. This makes
        // the emitted ^GFA dims equal the actual buffer bit-dims by
        // construction. An absent entry → placeholder (buffer: null, zone-sized
        // box, no ^GFA emitted downstream).
        const resolved = resolvedImages.get(c.id) ?? null
        const region = { xMm: zone.xMm, yMm: zone.yMm, wMm: zone.widthMm, hMm: zone.heightMm }
        const placement = c.placement ?? DEFAULT_PLACEMENT
        if (resolved) {
          const widthMm = (resolved.widthDots * 25.4) / profile.dpi
          const heightMm = (resolved.heightDots * 25.4) / profile.dpi
          const { anchorXMm, anchorYMm } = resolveAnchor(
            region,
            { wMm: widthMm, hMm: heightMm },
            placement
          )
          plans.push({
            kind: 'image',
            id: c.id,
            anchorXMm,
            anchorYMm,
            widthMm,
            heightMm,
            buffer: resolved.buffer,
            widthDots: resolved.widthDots,
            heightDots: resolved.heightDots,
          })
        } else {
          plans.push({
            kind: 'image',
            id: c.id,
            anchorXMm: zone.xMm,
            anchorYMm: zone.yMm,
            widthMm: zone.widthMm,
            heightMm: zone.heightMm,
            buffer: null,
          })
        }
        break
      }
      case 'symbol': {
        const r = planSymbol(zone, c, data, profile)
        plans.push(...r.plans)
        warnings.push(...r.warnings)
        break
      }
      default: {
        const _ex: never = c
        throw new Error(`buildRenderPlan: unknown primitive ${JSON.stringify(_ex)}`)
      }
    }
  }
  return { plans, warnings }
}
